import { useRef, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import { isPubliclyVisible, type TournamentStatus } from "@alpha/shared";
import type { ConflictWarning } from "@alpha/engine";
import { api } from "../../../lib/api";
import { useAdminBundle, useAdminMutation, useAdminRegistrations } from "../../../lib/adminData";
import { conflictsByGame } from "../../../lib/bundle";
import { formatDateRange } from "../../../lib/format";
import { useIsAdmin, useTournamentRole } from "../../../lib/me";
import { tournamentPath } from "../../../lib/routes";
import { derivePhases, type Caps, type PhaseReport } from "../../../lib/tournamentPhases";
import { readinessWarnings, statusActions, type TournamentAction } from "../../../lib/tournamentStatus";
import type { Bundle } from "../../../lib/types";
import { Button, ButtonLink, EmptyState, LoadingBlock, PageHeader, stagger } from "../../../components/ui";
import { PhaseRail } from "./PhaseRail";
import { ReadinessStrip } from "./ReadinessStrip";

/** What every phase page reads through `useOutletContext` (console spec §3.1). */
export interface TournamentContext {
  tid: string;
  bundle: Bundle;
  caps: Caps;
  conflicts: Map<string, ConflictWarning[]>;
  phases: PhaseReport;
  /** Null unless the caller may read the queue and it has loaded. */
  pendingRegistrations: number | null;
  /** One clock reading per shell render, so every page agrees on "today". */
  now: Date;
  /** Opens the readiness strip for a status change, or applies it when nothing is missing. */
  requestStatus: (next: TournamentStatus) => void;
  statusPending: boolean;
}

/**
 * One tournament's console (console spec §3): the header, the phase rail, and
 * an outlet for the phase page. Loads the bundle once and derives everything a
 * page needs, so a page never asks the role question or recomputes conflicts.
 */
export function TournamentShell() {
  const { tid = "" } = useParams();
  const bundleQ = useAdminBundle(tid);

  // Above the early returns, where the rules of hooks require them. Both read
  // the one cached session, so asking here costs no request.
  //
  // `useIsAdmin` answers false while that session is in flight, which would
  // otherwise flash a scorer's version of this console at an admin — but
  // AdminLayout waits on the same query before rendering this route, so it is
  // warm by the time this mounts.
  const isAdmin = useIsAdmin();
  const role = useTournamentRole(tid);
  const caps: Caps = {
    manage: isAdmin,
    runEvent: isAdmin || role === "tournamentAdmin",
    editEvent: isAdmin || role === "tournamentAdmin",
    score: isAdmin || role === "tournamentAdmin" || role === "tournamentScorer",
    schedule: isAdmin || role === "tournamentAdmin",
  };

  // The queue feeds the rail's Teams figure on every phase — but a scorer may
  // not read it, and a disabled query is absent rather than failed.
  const regsQ = useAdminRegistrations(tid, { enabled: caps.runEvent });

  // Archive keeps its two-step button; Publish and Mark complete go through
  // the strip. `nextRef` is a ref, NOT state: `mutate()` runs in the same
  // handler that sets it, and a state setter has not applied by then — the
  // mutationFn closure would still read the previous value and PUT
  // `{ status: null }`, which updateTournamentSchema rejects with a 400.
  const [confirming, setConfirming] = useState<TournamentStatus | null>(null);
  const [strip, setStrip] = useState<{ next: TournamentStatus; warnings: string[] } | null>(null);
  const nextRef = useRef<TournamentStatus | null>(null);
  const setStatus = useAdminMutation(tid, () =>
    api.put(`/admin/tournaments/${tid}`, { status: nextRef.current }),
  );

  // A scoped grant opens the console but only its own tournament, so this URL
  // is reachable by someone the bundle refuses. Guarded on there being nothing
  // to show: a query that already has data and then fails a background refetch
  // reports both at once, and an admin mid-edit should keep the page rather
  // than watch it turn into this.
  if (bundleQ.isError && !bundleQ.data)
    return (
      <EmptyState
        title="Couldn’t open this tournament"
        hint="It may have been deleted, or your access to it taken away. Everything you can reach is on the tournament list."
        className="mx-auto max-w-md"
      />
    );
  if (bundleQ.isLoading || !bundleQ.data) return <LoadingBlock />;
  const bundle = bundleQ.data;
  const t = bundle.tournament;

  // Plain calls, not useMemo: they sit after the early returns, where a hook
  // would violate the rules of hooks on the loading→loaded transition.
  const conflicts = conflictsByGame(bundle);
  const now = new Date();
  const pendingRegistrations =
    caps.runEvent && regsQ.data
      ? regsQ.data.registrations.filter((r) => r.status === "pending").length
      : null;
  const phases = derivePhases({ bundle, pendingRegistrations, conflicts, caps, now });

  const commit = (next: TournamentStatus) => {
    setStrip(null);
    setConfirming(null);
    nextRef.current = next;
    setStatus.mutate();
  };
  const requestStatus = (next: TournamentStatus) => {
    if (setStatus.isPending || strip) return;
    const warnings = readinessWarnings(next, bundle);
    if (warnings.length === 0) commit(next);
    else setStrip({ next, warnings });
  };
  const fire = (action: TournamentAction) => {
    if (action.confirm) {
      // Two-step confirm: there is no dialog primitive in this app, so the
      // button becomes its own confirmation, reverting on blur.
      if (confirming !== action.next) {
        setConfirming(action.next);
        return;
      }
      commit(action.next);
      return;
    }
    requestStatus(action.next);
  };

  const ctx: TournamentContext = {
    tid,
    bundle,
    caps,
    conflicts,
    phases,
    pendingRegistrations,
    now,
    requestStatus,
    statusPending: setStatus.isPending,
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={formatDateRange(t.startDate, t.endDate)}
        title={t.name}
        subtitle={t.location || undefined}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isPubliclyVisible(t.status) ? (
              <ButtonLink variant="secondary" to={tournamentPath(tid)}>
                View public page
              </ButtonLink>
            ) : (
              <span className="text-xs text-slate-400">
                {t.status === "archived"
                  ? "Public page is hidden while archived"
                  : "Public page appears once published"}
              </span>
            )}
            {/* Publishing is how an organizer runs their own event — but a
                scorer changes results, never the event, and gets no status
                buttons at all. */}
            {caps.editEvent
              ? statusActions(t.status).map((action) => (
                  <Button
                    key={action.next}
                    variant={confirming === action.next ? "primary" : action.variant}
                    onClick={() => fire(action)}
                    onBlur={() => setConfirming((c) => (c === action.next ? null : c))}
                    disabled={setStatus.isPending}
                  >
                    {confirming === action.next
                      ? `Confirm ${action.label.toLowerCase()}`
                      : action.label}
                  </Button>
                ))
              : null}
          </div>
        }
      />
      {strip ? (
        <ReadinessStrip
          label={strip.next === "published" ? "Publish" : "Complete"}
          warnings={strip.warnings}
          pending={setStatus.isPending}
          onConfirm={() => commit(strip.next)}
          onCancel={() => setStrip(null)}
        />
      ) : null}
      <PhaseRail tid={tid} boxes={phases.boxes} active={phases.active} status={t.status} />
      <div className="animate-rise" style={stagger(1)}>
        <Outlet context={ctx} />
      </div>
    </div>
  );
}
